import React, { useEffect } from 'react';
import { Check, AlertCircle, Info, X } from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';

const TOAST_DURATION = 3000;

const TOAST_STYLES: Record<'success' | 'error' | 'info', { bg: string; border: string; icon: typeof Check; iconColor: string; textColor: string }> = {
  success: {
    bg: 'bg-green-900/90',
    border: 'border-green-700/50',
    icon: Check,
    iconColor: 'text-green-400',
    textColor: 'text-green-200',
  },
  error: {
    bg: 'bg-red-900/90',
    border: 'border-red-700/50',
    icon: AlertCircle,
    iconColor: 'text-red-400',
    textColor: 'text-red-200',
  },
  info: {
    bg: 'bg-blue-900/90',
    border: 'border-blue-700/50',
    icon: Info,
    iconColor: 'text-blue-400',
    textColor: 'text-blue-200',
  },
};

export function ScreenToastBar() {
  const toasts = useSlotScreenGeneratorStore(s => s.toasts);
  const removeToast = useSlotScreenGeneratorStore(s => s.removeToast);

  // Auto-dismiss after TOAST_DURATION
  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((toast) =>
      setTimeout(() => removeToast(toast.id), TOAST_DURATION)
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts, removeToast]);

  if (toasts.length === 0) return null;

  return (
    <div className="absolute bottom-12 right-4 z-[120] space-y-2 pointer-events-none max-w-xs">
      {toasts.map((toast) => {
        const style = TOAST_STYLES[toast.type] ?? TOAST_STYLES.info;
        const IconComponent = style.icon;

        return (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-lg ${style.bg} border ${style.border} text-xs shadow-lg animate-slideUp`}
          >
            <IconComponent size={14} className={`${style.iconColor} flex-shrink-0`} />
            <span className={`${style.textColor} flex-1 min-w-0`}>
              {toast.message}
            </span>
            <button
              onClick={() => removeToast(toast.id)}
              className={`${style.iconColor} hover:text-white flex-shrink-0 transition-colors`}
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
